import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { getTheme } from "../styles/theme";

// A React Context rather than a redux slice (unlike systemSettingsSlice
// etc.) — this needs to react to a live `matchMedia` listener (the OS
// theme can change while the tab is open) and feed straight into
// `ThemeProvider`, which is simplest as context state co-located with the
// provider itself; there's no server round-trip or cross-slice state to
// justify redux here.
//
// Three possible preferences, matching the "system preference by default,
// with a manual override" requirement:
//   - override === "light" | "dark": the user explicitly picked one
//   - override === null: follow the OS/browser's prefers-color-scheme
// `resolvedMode` is always a concrete "light" | "dark" — the one thing
// every consumer (ThemeProvider, the toggle UI, the data-theme-mode
// attribute) actually needs.
const STORAGE_KEY = "colorModeOverride";

const readStoredOverride = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
};

const getSystemPrefersDark = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : false;

const ColorModeContext = createContext(null);

export const ColorModeProvider = ({ children }) => {
  const [override, setOverrideState] = useState(readStoredOverride);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

  // Live-updates if the OS theme changes while the app is open (e.g. a
  // scheduled light/dark switch, or the user flips it in OS settings) —
  // only matters while no manual override is set, but the listener stays
  // attached regardless so it's ready the moment an override is cleared.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemPrefersDark(e.matches);
    // Safari < 14 only supports addListener/removeListener; modern browsers
    // support addEventListener. Both are cheap to attach.
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, []);

  const resolvedMode = override || (systemPrefersDark ? "dark" : "light");

  // Mirrored onto <html data-theme-mode="..."> for the handful of things
  // outside MUI's reach (global.css's plain CSS, the pre-React bootstrap
  // script in index.html that prevents a flash of the wrong theme). The
  // bootstrap script also sets an inline `background-color` directly on
  // <html> (to paint correctly before any CSS loads) — that inline style
  // only ever runs once, before React mounts, so it has to be kept in sync
  // here too on every toggle. Without this, the toggle still re-themes
  // body/every component correctly via React, but <html>'s own background
  // stays stuck at whatever it was on page load — invisible most of the
  // time since body covers it, but visible as a stale-colored flash/edge
  // in overscroll or before body paints, and was reported as "needs a
  // refresh to update the bg."
  useEffect(() => {
    document.documentElement.dataset.themeMode = resolvedMode;
    document.documentElement.style.backgroundColor = resolvedMode === "dark" ? "#0B1220" : "#F4F6FA";
  }, [resolvedMode]);

  const setOverride = useCallback((value) => {
    // value is "light" | "dark" | null ("system")
    setOverrideState(value);
    try {
      if (value) localStorage.setItem(STORAGE_KEY, value);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — preference just won't persist across reloads */
    }
  }, []);

  const theme = useMemo(() => getTheme(resolvedMode), [resolvedMode]);

  const value = useMemo(
    () => ({ mode: resolvedMode, override, setOverride, isSystem: override === null }),
    [resolvedMode, override, setOverride],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

// Throws instead of returning a silent default — a toggle rendered outside
// the provider is a bug (it would otherwise show a 3rd, meaningless state)
// and should fail loudly in development rather than silently do nothing.
export const useColorMode = () => {
  const ctx = useContext(ColorModeContext);
  if (!ctx) throw new Error("useColorMode must be used within a ColorModeProvider");
  return ctx;
};
